import { API_URL } from '../config';

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+\-.]*:\/\//i;

/**
 * Ensures thumbnails or other media values resolve to an absolute URL.
 * Supports legacy `/uploads/...` paths that were stored without a host.
 */
export const resolveMediaUrl = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
        return trimmed;
    }

    if (trimmed.startsWith('//')) {
        const fallbackProtocol = typeof window !== 'undefined' && window.location?.protocol
            ? window.location.protocol
            : 'https:';
        return `${fallbackProtocol}${trimmed}`;
    }

    const normalizedBase = API_URL.replace(/\/+$/, '');
    const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

    try {
        return new URL(normalizedPath, `${normalizedBase}/`).toString();
    } catch {
        return `${normalizedBase}${normalizedPath}`;
    }
};

// Media URL attributes in rich text HTML (tiptap output uses double quotes;
// single quotes handled for legacy/pasted markup): src on img/video/audio
// and their <source> children, a video's poster, and an embed's
// data-poster. An iframe's own src is a third-party player URL and is never
// rewritten.
const MEDIA_URL_ATTRIBUTES = {
    img: ['src'],
    video: ['src', 'poster'],
    audio: ['src'],
    source: ['src'],
    iframe: ['data-poster'],
};

// A whole start tag; quoted attribute values may contain '>'.
const MEDIA_TAG_PATTERN = /<(img|video|audio|source|iframe)\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;

const URL_ATTRIBUTE_PATTERN = /(\s)(src|poster|data-poster)=("([^"]*)"|'([^']*)')/gi;

const MEDIA_TAG_HINT = /<(?:img|video|audio|source|iframe)\b/i;

const rewriteMediaSources = (html, rewriteSrc) => {
    if (typeof html !== 'string' || !MEDIA_TAG_HINT.test(html)) {
        return typeof html === 'string' ? html : '';
    }

    return html.replace(MEDIA_TAG_PATTERN, (tag, tagName) => {
        const attributes = MEDIA_URL_ATTRIBUTES[tagName.toLowerCase()];
        return tag.replace(URL_ATTRIBUTE_PATTERN, (match, space, name, quoted, doubleQuoted, singleQuoted) => {
            if (!attributes.includes(name.toLowerCase())) {
                return match;
            }
            const src = doubleQuoted !== undefined ? doubleQuoted : singleQuoted;
            const quote = quoted.charAt(0);
            return `${space}${name}=${quote}${rewriteSrc(src)}${quote}`;
        });
    });
};

/**
 * Rewrites server-relative media srcs and posters (`/api/images/...`,
 * `/uploads/...`) in rich text HTML to absolute API URLs so they display
 * regardless of which host serves the frontend. Absolute, protocol-relative
 * and data: URLs are untouched.
 */
export const resolveRichTextMedia = (html) => rewriteMediaSources(html, (src) => {
    if (!src || !src.startsWith('/') || src.startsWith('//')) {
        return src;
    }
    return resolveMediaUrl(src);
});

/**
 * Inverse of resolveRichTextMedia: strips the current API origin from media
 * srcs and posters so stored content keeps portable server-relative paths
 * (same policy as thumbnails) and survives host/protocol changes.
 */
export const relativizeRichTextMedia = (html) => {
    const normalizedBase = API_URL.replace(/\/+$/, '');
    if (!normalizedBase) {
        return typeof html === 'string' ? html : '';
    }

    return rewriteMediaSources(html, (src) => {
        if (src && src.startsWith(`${normalizedBase}/`)) {
            return src.slice(normalizedBase.length);
        }
        return src;
    });
};
